import React, { useContext, useEffect, useRef, useState } from "react";
import { StyleSheet, View, Dimensions } from "react-native";
import { Modal, Text, TouchableOpacity } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import Spinner from "../../../components/shared/Spinner";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useToast } from "react-native-toast-notifications";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { translateFields, useTranslation, translateHeaders } from "../../../utils/useTranslation";
import { chinaShipmentTranslationMap } from "../../../utils/translations";
import {
  searchIncomingShipment,
  updateIncomingPage,
  updateIncomingSort,
} from "../../../redux/reducers/chinaTrackerReducer";
import {
  approveIncomingChinaShipment,
  getChinaIncomingShipments,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useIsFocused } from "@react-navigation/native";
import { ChinaTrackerDataTableWithPagination3 } from "../../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination3";
import teamsConfig from "../../../utils/constants/teams";
import rolesConfig from "../../../utils/constants/roles";
import { useAppTheme } from "../../../hooks/useAppTheme";

interface ChinaShipmentIncomingOrdersProps {
  visible: boolean;
  onClose: any;
  order: string;
  handleEdit: any;
}

const tableHeadersData = {
  containerNo: {
    name: "Container",
    width: 0.3,
  },
  location: {
    name: "Location",
    width: 0.2,
  },
  ETD: {
    name: "ETD",
    width: 0.25,
  },
  ETA: {
    name: "ETA",
    width: 0.25,
  },
  shippingLine: {
    name: "Shipping Line",
    width: 0.3,
  },
  sealNo: {
    name: "Seal No.",
    width: 0.3,
  },
  totalItems: {
    name: "Total Items",
    width: 0.25,
    textAlign: "right" as const,
    justifyContent: "flex-end" as const,
  },
  totalQty: {
    name: "Total Qty",
    width: 0.3,
    textAlign: "right" as const,
    justifyContent: "flex-end" as const,
  },
  generatedBy: {
    name: "Created By",
    width: 0.25,
  },
  createdAt: {
    name: "Created Date",
    width: 0.35,
  },
  Checkbox: {
    name: "Checkbox",
    width: 0.12,
    display: "none",
  },
  ShipmentEdit: {
    name: "ShipmentEdit",
    width: 0.12,
    display: "none",
  },
};

const tableHeadersDataChina = {
  containerNo: {
    name: "Container",
    width: 0.3,
  },
  location: {
    name: "Location",
    width: 0.2,
  },
  ETD: {
    name: "ETD",
    width: 0.25,
  },
  ETA: {
    name: "ETA",
    width: 0.25,
  },
  shippingLine: {
    name: "Shipping Line",
    width: 0.3,
  },
  sealNo: {
    name: "Seal No.",
    width: 0.3,
  },
  totalItems: {
    name: "Total Items",
    width: 0.25,
    textAlign: "right" as const,
    justifyContent: "flex-end" as const,
  },
  totalQty: {
    name: "Total Qty",
    width: 0.3,
    textAlign: "right" as const,
    justifyContent: "flex-end" as const,
  },
  generatedBy: {
    name: "Created By",
    width: 0.25,
  },
  createdAt: {
    name: "Created Date",
    width: 0.35,
  },
  ShipmentEdit: {
    name: "ShipmentEdit",
    width: 0.12,
    display: "none",
  },
};

const nestedHeaders = {
  orderNumber: {
    name: "Order No.",
    width: 0.35,
  },
  chinaItemCode: {
    name: "China Item Code",
    width: 0.35,
  },
  itemCode: {
    name: "Item Code",
    width: 0.35,
  },
  itemDescription: {
    name: "Item Name",
    width: 0.55,
  },
  qtyTotal: {
    name: "Qty",
    width: 0.2,
    textAlign: "right" as const,
    justifyContent: "flex-end" as const,
  },
  gwTotal: {
    name: "Gross Weight",
    width: 0.3,
    textAlign: "right" as const,
    justifyContent: "flex-end" as const,
  },
  nwTotal: {
    name: "Net Weight",
    width: 0.3,
    textAlign: "right" as const,
    justifyContent: "flex-end" as const,
  },
};


const ChinaShipmentIncomingOrders: React.FC<
  ChinaShipmentIncomingOrdersProps
> = ({ visible, onClose, order, handleEdit }: any) => {
  const { colors: themeColors } = useAppTheme();
  const styles = createStyles(themeColors);
  const listRef = useRef<ScrollView>(null);
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get("window").width,
  );
  const [screenHeight, setScreenHeight] = useState(
    Dimensions.get("window").height,
  );
  const [tableHeaders, setTableHeaders] =
    useState<Partial<typeof tableHeadersData>>(tableHeadersData);

  const {
    loadingAction,
    loading,
    shipments,
    totalCount,
    totalPages,
    pageSize,
    page,
    sort,
    status,
    search,
  } = useSelector((state: any) => state.chinaTracker.shipments.incoming);

  const { userInfo, currentRole } = useSelector(
    (state: any) => state.configurations,
  );
  const isAdminOrOwner = [
    rolesConfig.roles.admin,
    rolesConfig.roles.owner,
  ].includes(currentRole?.toUpperCase());
  const userTeam = userInfo?.team?.toUpperCase();

  const canAccessEdit =
    [teamsConfig.teams.purchasing].includes(userTeam) || isAdminOrOwner;

  useEffect(() => {
    if (!canAccessEdit) {
      setTableHeaders(tableHeadersDataChina);
    }
  }, [canAccessEdit]);

  const { t } = useTranslation();

  const translatedNestedHeaders = translateHeaders(
    nestedHeaders,
    t,
    chinaShipmentTranslationMap,
  );

  const enrichedIncomingShipments = React.useMemo(() => {
    if (!shipments || !Array.isArray(shipments)) return [];
    return shipments.map((shipment: any) => {
      const items = (shipment.items ?? []).map((item: any) => {
        return {
          ...item,
          orderNumber: item.orderNumber || item.orderNo || "",
          chinaItemCode: item.chinaItemCode || "",
          itemCode: item.itemCode || "",
          itemDescription: item.itemDescription || item.itemName || "",
          qtyTotal: item.qtyTotal !== undefined ? item.qtyTotal : (item.shipmentQty !== undefined ? item.shipmentQty : 0),
          gwTotal: item.gwTotal !== undefined ? item.gwTotal : 0,
          nwTotal: item.nwTotal !== undefined ? item.nwTotal : 0,
        };
      });
      const totalItems = items.length;
      const totalQty = items.reduce((acc: number, item: any) => acc + (item.qtyTotal || 0), 0);
      return {
        ...shipment,
        items,
        totalItems,
        totalQty,
      };
    });
  }, [shipments, t]);

  const dispatch = useDispatch<any>();
  const [actionRows, setActionRows] = useState<any>([]);
  const [isRefresh, setRefresh] = useState<boolean>(false);

  const toast = useToast();
  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext,
  );
  const [, setContentOffSet] = useState<number>(0);

  const isFocused = useIsFocused();

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      dispatch(updateIncomingPage(newPage));
    }
  };
  const handleSort = (key: any) => {
    let direction = "asc";
    if (sort.key === key && sort.direction === "asc") {
      direction = "desc";
    }
    dispatch(updateIncomingSort({ key, direction }));
  };

  const fetchShipments = async () => {
    dispatch(
      getChinaIncomingShipments({
        page: page,
        pageSize: pageSize,
        sort: sort.direction === "asc" ? sort.key : "-" + sort.key,
        status: status,
        search: search,
      }),
    );
  };

  const navigateToOrder = (order: any, action: string) => {
    if (action === "accept") {
      const orders: any = [];
      for (const orderKey of order) {
        orders.push({
          _id: orderKey._id,
          status: "APPROVED",
        });
      }
      dispatch(approveIncomingChinaShipment({ orders }))
        .unwrap()
        .then(() => {
          toast.show("Incoming order approved successfully", {
            type: "success",
          });

          setActionRows([]);

          return dispatch(
            getChinaIncomingShipments({
              page,
              pageSize,
              sort: sort.direction === "asc" ? sort.key : "-" + sort.key,
              status,
              search,
            }),
          ).unwrap();
        })
        .catch((error: any) => {
          toast.show(
            error || "Error occurred during approval of incoming order",
            { type: "danger" },
          );
        });
    } else if (action === "shipment_edit") {
      handleEdit(order);
    }
  };

  useEffect(() => {
    if (isFocused) {
      fetchShipments();
    }
  }, [isFocused, sort, status, pageSize, page, search]);

  // Update dimensions when screen size changes
  useEffect(() => {
    const updateDimensions = () => {
      const { width, height } = Dimensions.get("window");
      setScreenWidth(width);
      setScreenHeight(height);
    };
    const dimensionsListener = Dimensions.addEventListener(
      "change",
      updateDimensions,
    );
    return () => dimensionsListener.remove();
  }, []);

  const handleScroll = (event: any) => {
    setContentOffSet(Math.floor(event.nativeEvent?.contentOffset?.y) ?? 0);
  };

  const getModalWidth = () => {
    if (isSmallDevice) return "100%";
    if (isMediumDevice) return "94%";
    return "92%";
  };

  const getInnerContainerWidth = () => "100%";

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleSearch = (searchedText: string) => {
    dispatch(updateIncomingPage(1));
    if (searchedText) {
      dispatch(searchIncomingShipment(searchedText));
    } else {
      dispatch(searchIncomingShipment(""));
    }
  };

  const tableContent = (
    <>
      {loading || loadingAction ? (
        <View>
          <Spinner
            size="small"
            color="#74BBFF"
            styles={{ flexDirection: "row", margin: "20%" }}
          />
        </View>
      ) : (
        <View>
          <ChinaTrackerDataTableWithPagination3
            isToExpandAllRows={false}
            isRowsExpandable={true}
            headers={translateHeaders(
              tableHeaders,
              t,
              chinaShipmentTranslationMap,
            )}
            rows={enrichedIncomingShipments}
            itemsPerPage={10}
            isDisplayTimeWithDate={false}
            useModal={true}
            isDetail={false}
            isEdit={false}
            isDelete={false}
            actionRows={actionRows}
            setActionRows={setActionRows}
            itemTracker={true}
            setModalVisible={() => { }}
            handleRowClick={() => { }}
            setSelected={() => { }}
            handleDeleteRow={() => { }}
            t={t}
            page={page - 1}
            setPage={handlePageChange}
            handleSortColumn={handleSort}
            operationActions={navigateToOrder}
            nestedHeader={translatedNestedHeaders}
            nestedItemKeyValue="items"
            rowKeyValue="containerNo"
            columnsToNotHaveThousandSeparator={["Order No.", "Order No", "orderNo", "Total Items", "Order Date", "Request Date", "Status", "status", "orderNumber", "Item Name", "China Item Code", "Item Code", "itemDescription", "chinaItemCode", "itemCode"]}
          />
        </View>
      )}
    </>
  );

  const titleAndSearch = (
    <View
      style={[
        styles.titleContainer,
        isSmallDevice && { flexDirection: "column" },
      ]}
    >
      <Text style={[styles.titleText, isSmallDevice && styles.titleTextSmall, { color: themeColors.text }]}>
        {t("incomingShipment")}
      </Text>
      {!isSmallDevice && (
        <View style={styles.searchContainer}>
          <LabelBannerWithSearchBar
            titleName={``}
            handleSearch={handleSearch}
            setRefresh={() => {
              dispatch(updateIncomingPage(1));
              fetchShipments();
            }}
            dataToExport={shipments}
            fileName="ItemTrackerScreen"
            sheetName="Item Tracker Report Screen"
          />
        </View>
      )}
    </View>
  );

  const actionButtons = (showCount: boolean) => (
    <View style={styles.actionButtonsContainer}>
      {showCount && (
        <Text style={{ display: "flex", alignItems: "center", color: themeColors.text }}>
          {actionRows?.length}/10{" "}
          {translateFields("ItemsSelected", t, chinaShipmentTranslationMap)}
        </Text>
      )}
      <TouchableOpacity
        style={
          actionRows.length > 0
            ? styles.actionButton
            : styles.disabledActionButton
        }
        onPress={() => navigateToOrder(actionRows, "accept")}
        disabled={actionRows.length === 0}
      >
        <Text style={styles.actionButtonText}>
          {translateFields("Accept", t, chinaShipmentTranslationMap)}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const footer = (
    <View style={[styles.footer, isSmallDevice && styles.footerSmall]}>
      <TouchableOpacity
        onPress={onClose}
        style={[styles.closeButton, isSmallDevice && styles.buttonSmall]}
      >
        <Text style={[isSmallDevice ? styles.buttonTextSmall : undefined, { color: themeColors.text }]}>
          {t("close")}
        </Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <>
      <Modal visible={visible} transparent animationType="slide">
        <View style={styles.overlay}>
          {isLargeDevice ? (
            <View style={[styles.modalContainer, { width: getModalWidth() }]}>
              <View
                style={[
                  styles.modalContainerSecond,
                  { width: getInnerContainerWidth(), height: "100%" },
                ]}
              >
                {titleAndSearch}
                {canAccessEdit && actionButtons(true)}
                <ScrollView onScroll={handleScroll} ref={listRef}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator
                    contentContainerStyle={{
                      minWidth: "100%",
                    }}
                  >
                    <View
                      style={[
                        styles.containerContent,
                        { minWidth: "100%" },
                      ]}
                    >
                      {tableContent}
                    </View>
                  </ScrollView>
                </ScrollView>
                {footer}
              </View>
            </View>
          ) : (
            <View
              style={[
                styles.modalContainerSecond,
                { width: "100%", height: "90%" },
              ]}
            >
              {titleAndSearch}
              {actionButtons(false)}
              <ScrollView onScroll={handleScroll} ref={listRef}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator
                  contentContainerStyle={{
                    minWidth: "100%",
                  }}
                >
                  <View
                    style={[
                      styles.containerContent,
                      { minWidth: "100%" },
                    ]}
                  >
                    {tableContent}
                  </View>
                </ScrollView>
              </ScrollView>
              {footer}
            </View>
          )}
        </View>
      </Modal>
    </>
  );
};

function createStyles(colors: any) {
  return StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.15)",
    paddingVertical: 16,
    paddingHorizontal: 8,
  },
  modalContainer: {
    backgroundColor: colors.surface,
    padding: 20,
    elevation: 12,
    borderRadius: 12,
    width: "100%",
    maxHeight: "95%",
  },
  modalContainerSecond: {
    backgroundColor: colors.surface,
    elevation: 12,
    borderRadius: 12,
    padding: 16,
    width: "100%",
    height: "95%",
  },
  titleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.surface,
    paddingVertical: 12,
    marginBottom: 12,
  },
  titleText: {
    fontSize: 18,
    fontWeight: "700",
  },
  titleTextSmall: {
    fontSize: 14,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  actionButtonsContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 12,
    gap: 10,
  },
  actionButton: {
    backgroundColor: "#56B7FD",
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  disabledActionButton: {
    backgroundColor: colors.surfaceSubtle,
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  actionButtonText: {
    fontSize: 10,
    color: "#fff",
    fontWeight: "700",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: 20,
    padding: 25,
  },
  footerSmall: {
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 5,
    width: 120,
    alignItems: "center",
  },
  buttonSmall: {
    padding: 8,
    paddingHorizontal: 12,
  },
  buttonTextSmall: {
    fontSize: 12,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    width: "100%",
    paddingHorizontal: 0,
    paddingBottom: 0,
  },
  filterChipsRow: {
    marginBottom: 8,
  },
  filterLabel: {
    fontSize: 10,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 5,
    marginHorizontal: 3,
  },
  selectedFilter: {
    fontSize: 10,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderWidth: 1,
    borderColor: "#56B7FD",
    borderRadius: 5,
    marginHorizontal: 3,
    backgroundColor: "#56B7FD",
    color: "#fff",
  },
});
}

export default ChinaShipmentIncomingOrders;
